import { debounce } from 'lodash'
import { ArrowLeft, Search, X } from 'lucide-react'
import React, { useMemo, useState, useRef, useEffect } from 'react'

import { Input } from 'src/shared/components/ui/inputs/input'
import { cn } from 'src/shared/utils/cn'

interface SearchInputProps {
  onChange: (value: string) => void
  delay?: number
  placeholder?: string
  className?: string
  children?: React.ReactNode
  searchTerm?: string
  variant?: 'default' | 'unstyled'
  onFocus?: () => void
  onBlur?: () => void
  clearOnClickOutside?: boolean
  autoFocus?: boolean
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onChange,
  delay = 600,
  placeholder,
  className,
  children,
  searchTerm,
  variant = 'default',
  onFocus,
  onBlur,
  clearOnClickOutside = false,
  autoFocus,
}) => {
  const [inputValue, setInputValue] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  const debouncedHandleChange = useMemo(
    () =>
      debounce((newValue: string) => {
        onChange(newValue)
      }, delay),
    [onChange, delay],
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    debouncedHandleChange(newValue)
  }

  const clearSearch = () => {
    setInputValue('')
    onChange('')
  }

  useEffect(() => {
    if (!clearOnClickOutside) return

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setTimeout(() => {
          setInputValue('')
          onChange('')
        }, 100)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onChange, clearOnClickOutside])

  // Clear input when onChange is called with empty string
  useEffect(() => {
    const unsubscribe = debouncedHandleChange.cancel
    return () => unsubscribe()
  }, [debouncedHandleChange])

  useEffect(() => {
    if (searchTerm === '') {
      setInputValue('')
    }
  }, [searchTerm])

  return (
    <div ref={containerRef} className={`relative w-full`}>
      <div className="relative">
        {inputValue ? (
          <ArrowLeft
            className="absolute left-3 top-1/2 size-5 -translate-y-1/2 cursor-pointer text-muted"
            onClick={clearSearch}
          />
        ) : (
          <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted" />
        )}
        <Input
          type="text"
          value={inputValue}
          onChange={handleChange}
          placeholder={placeholder}
          className={cn(
            'pl-10',
            variant === 'default' && 'h-11 rounded-full shadow-sm',
            inputValue && 'pr-9',
            className,
          )}
          onFocus={onFocus}
          onBlur={onBlur}
          hidePlaceholderOnFocus
          autoFocus={autoFocus}
        />
        {inputValue && (
          <X
            className="absolute right-3 top-1/2 size-5 -translate-y-1/2 cursor-pointer text-muted hover:text-primary"
            onClick={clearSearch}
          />
        )}
      </div>
      {children}
    </div>
  )
}
